import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { AppKyc } from "../../types";
import { computeAppKycAnalytics } from "../../utils/appKycAnalytics";
import { isLocalDevAppMode } from "../../utils/appMode";
import { apiService } from "../../api/services/apiService";

const tablePaperSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

interface AppKycAnalyticsPanelProps {
  appKycs: AppKyc[];
  onCleanupComplete: () => void;
}

const AppKycAnalyticsPanel: React.FC<AppKycAnalyticsPanelProps> = ({
  appKycs,
  onCleanupComplete,
}) => {
  const analytics = useMemo(() => computeAppKycAnalytics(appKycs), [appKycs]);
  const showDevCleanup = isLocalDevAppMode();
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  const [cleanupSuccess, setCleanupSuccess] = useState<string | null>(null);

  const activeShare = analytics.total > 0 ? (analytics.active / analytics.total) * 100 : 0;

  const handleDevCleanup = async () => {
    setCleaning(true);
    setCleanupError(null);
    try {
      const deleted = await apiService.devCleanupAppKycs();
      setCleanupSuccess(`Removed ${deleted} API key record(s).`);
      setCleanupOpen(false);
      onCleanupComplete();
    } catch (err: unknown) {
      setCleanupError(err instanceof Error ? err.message : "Dev cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <>
      {cleanupSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCleanupSuccess(null)}>
          {cleanupSuccess}
        </Alert>
      )}
      {cleanupError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCleanupError(null)}>
          {cleanupError}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        API key analytics
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <VpnKeyIcon color="primary" fontSize="small" />
                <Typography color="text.secondary" variant="body2">
                  Total API keys
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>
                {analytics.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <AnalyticsIcon color="success" fontSize="small" />
                <Typography color="text.secondary" variant="body2">
                  Active keys
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {analytics.active}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={activeShare}
                sx={{ mt: 1, height: 6, borderRadius: 1 }}
                color="success"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Inactive / disabled / blocked
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {analytics.inactive + analytics.disabled + analytics.blocked}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {analytics.inactive} inactive · {analytics.disabled} disabled ·{" "}
                {analytics.blocked} blocked
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <WarningAmberIcon color="warning" fontSize="small" />
                <Typography color="text.secondary" variant="body2">
                  Expiry watch
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={700} color="warning.main">
                {analytics.expiringWithin30Days}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                expiring in 30 days · {analytics.expired} expired ·{" "}
                {analytics.noExpiryDate} no expiry
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, ...tablePaperSx }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Keys by gateway type
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Gateway type</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.byGatewayType.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No API keys yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    analytics.byGatewayType.map((row) => (
                      <TableRow key={row.gatewayType}>
                        <TableCell>{row.gatewayType}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, ...tablePaperSx }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Top companies
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell align="right">Keys</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.byCompany.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No company names recorded.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    analytics.byCompany.map((row) => (
                      <TableRow key={row.companyName}>
                        <TableCell>{row.companyName}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, ...tablePaperSx }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Contact coverage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {analytics.withContactEmail} of {analytics.total} keys have a contact email on file.
        </Typography>
      </Paper>

      {showDevCleanup && (
        <Paper
          sx={{
            p: 3,
            mt: 3,
            ...tablePaperSx,
            borderColor: "warning.light",
            bgcolor: "warning.50",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Dev cleanup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Remove all API key records for the current tenant. Available only in local dev mode
            when dev tools are enabled on the API.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setCleanupOpen(true)}
            disabled={analytics.total === 0 || cleaning}
            sx={{ textTransform: "none" }}
          >
            Delete all API keys ({analytics.total})
          </Button>
        </Paper>
      )}

      <Dialog open={cleanupOpen} onClose={() => !cleaning && setCleanupOpen(false)}>
        <DialogTitle>Dev cleanup — delete all API keys?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes all {analytics.total} App KYC / API key record(s) for the
            current tenant. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupOpen(false)} disabled={cleaning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDevCleanup()}
            disabled={cleaning}
          >
            {cleaning ? "Deleting..." : "Delete all"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppKycAnalyticsPanel;
